/*
 * EntityPage3.tsx
 *
 * Created by Dr. Maximillian Dornseif 2023-10-08 in huWaWi3 33.1.0
 * Copyright (c) 2021-2023 HUDORA GmbH
 */

import { ICommandBarItemProps } from '@fluentui/react';
import { Pivot, PivotItem } from '@fluentui/react/lib/Pivot';
import { useSchema , LadeChecker } from '@hudora/hd-react-components';
import { assertIsDefined } from 'assertate-debug';
import { useTitle } from 'fluentui-hooks';
import { DocumentNode } from 'graphql';
import React, { PropsWithChildren, ReactNode } from 'react';
import { useQuery } from 'urql';
import { Link, Route, useLocation, useRouter } from 'wouter';

import { EntityPageInner3 } from './EntityPageInner3';

export interface IEntitySubPage3 {
  title: string;
  path?: string;
  component?: React.ReactElement;
  render: (entity) => ReactNode;
}

export interface IEntityPage3Props {
  nr: string; // Designator
  basePath: string;
  subPages?: IEntitySubPage3[];
  commandBarItems?: ICommandBarItemProps[];
  mutation?: DocumentNode;
  entityQuery: DocumentNode;
  schemaName: string;

  renderInner?: (entity) => ReactNode; // defaults to <EntityPageInner3 />
  renderEntity?: (entity, schemaName) => ReactNode;
  renderAnmerkung?: (entity, schemaName) => ReactNode;
  renderRightSidebar?: (entity, schemaName) => ReactNode;
  renderBottom?: (entity, schemaName) => ReactNode;

  children?: React.ReactNode;
  // editMutation?: DocumentNode // bearbeiten einer SubEntitiy
  // forDesignator?: string // zu welchem Entity soll das SubEntity erzeugt werden?
}
/** Anzeige für "Entities",  mit Tabbar,
 * In der können SubEinheiten wie z.B. Lieferadressen eines Kunden,
 * angezeigt werden. Dazu dient <SubEntityDisplay>
 **/
export const EntityPageWrapper3 = (props: PropsWithChildren<IEntityPage3Props>) => {
  const [location, navigate] = useLocation();
  const router = useRouter();
  const { schema } = useSchema(props.schemaName);
  const [result] = useQuery({
    query: props.entityQuery,
    pause: !props.nr,
    variables: { nr: props.nr },
  });
  const { data, fetching, error } = result;

  const innerBasePath = `${props.basePath}/${props.nr}`;
  const title =
    data?.entity?.name && data?.entity?.designator
      ? `${data?.entity?.name || ''} (${data?.entity?.designator})`
      : data?.entity?.designator
      ? `${data?.entity?.designator}`
      : props.nr;
  useTitle(`${data?.entity?.designator || props.nr} ${schema?.title || ''}`);

  let selectedKey = '';
  const pivotItems: React.ReactElement[] = [
    <PivotItem
      key={`item_Stammdaten`}
      itemKey={`item_Stammdaten`}
      headerText="Stammdaten"
      ariaLabel="Stammdaten"
      headerButtonProps={{ href: innerBasePath }}
    />,
  ];

  const pivotPages: React.ReactElement[] = [];
  for (const page of props.subPages || []) {
    const key = `pivot-${page.title}`;
    const path = `${innerBasePath}/${page.path}`;
    const [match] = router.matcher(path, location);
    assertIsDefined(page.render, `page.render() für ${page.title} fehlt.`);
    if (match) {
      selectedKey = key;
    }
    pivotItems.push(
      <PivotItem
        key={key}
        itemKey={key}
        headerText={page.title}
        ariaLabel={page.title}
        headerButtonProps={{ href: path }}
      />
    );
    pivotPages.push(
      <Route key={key} path={path}>
        {data?.entity ? (
          <React.Fragment key={key}>{page.render(data?.entity)}</React.Fragment>
        ) : (
          <span>(noch) keine Daten ...</span>
        )}
      </Route>
    );
  }

  const pivot = (
    <Pivot
      headersOnly
      selectedKey={selectedKey}
      onLinkClick={(item) => navigate(`${item?.props?.headerButtonProps?.href}`)}
    >
      {pivotItems}
    </Pivot>
  );

  if (!props.nr) {
    return (
      <span
        dangerouslySetInnerHTML={{
          __html: `<!-- EntityPage hat keine nr ${JSON.stringify(props)}} -->`,
        }}
      />
    );
  }

  const dataError = !data?.entity
    ? new Error(`Keine Entity Daten für "${props.nr}" erhalten (${error || '?'}))`)
    : undefined;

  if (fetching && !data) {
    return <LadeChecker loading={fetching} error={error} label={`Lade Daten für Entity ${props.nr} …`} />;
  }
  const renderInner =
    props.renderInner ||
    ((e) => {
      return e?.designator ? (
        <EntityPageInner3
          entity={e}
          schemaName={props.schemaName}
          mutation={props.mutation}
          commandBarItems={props.commandBarItems}
          renderEntity={props.renderEntity}
          renderAnmerkung={props.renderAnmerkung}
          renderRightSidebar={props.renderRightSidebar}
          renderBottom={props.renderBottom}
        >
          {props.children}
        </EntityPageInner3>
      ) : null;
    });

  let frag = (<span>keine Daten</span>) as ReactNode;
  if (data?.entity) {
    frag = renderInner(data?.entity);
  }

  return (
    <LadeChecker loading={false} error={dataError} label={`lade Daten für Entity ${props.nr} …`}>
      <>
        <h1 aria-label="EntityName">
          <Link href={props.basePath}>{schema.title || ''}</Link> {title || 'Lade den Namen…'}
        </h1>

        {pivotPages.length > 0 ? pivot : null}

        <Route path={innerBasePath} key={`item_Stammdaten`}>
          {frag as any}
        </Route>
        {pivotPages}
      </>
    </LadeChecker>
  );
};
